import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Box,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import genericStyles from "../Styles/genericStyles.module.css";
import { EWidths } from "../Utils/Enums";
import { GenericModalProps } from "../Utils/Types";
import { isMobile } from "../Utils/Utils";
import { TransitionProps } from "@mui/material/transitions";
import { forwardRef, ReactElement, Ref } from "react";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement;
  },
  ref: Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function GenericModal(props: GenericModalProps) {
  const {
    open,
    children,
    className,
    onClose,
    actions,
    title,
    maxWidth,
    otherOptions,
    width,
  } = props;
  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      className={`${className ? className : ""}`}
      style={props.style}
      maxWidth={maxWidth ? maxWidth : EWidths.SM}
      fullWidth={isMobile() && !maxWidth && !className && !props.style}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 24px 48px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
          backdropFilter: "blur(10px)",
          width: width || "auto",
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      {title && typeof title === "string" && title.trim() !== "" ? (
        <DialogTitle
          className={genericStyles.genericModalTitleContainer}
          sx={{
            background:
              "linear-gradient(135deg, primary.main 0%, primary.dark 100%)",
            color: "primary.dark",
            borderRadius: 0,
            position: "relative",
            padding: "12px 24px",
            minHeight: "56px",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
            },
          }}
        >
          <div className={genericStyles.genericModalExtraOption}>
            <Typography
              variant="h6"
              sx={{
                margin: 0,
                fontWeight: 600,
                color: "primary.dark",
              }}
            >
              {title}
            </Typography>
            <div>{otherOptions}</div>
          </div>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "primary.dark",
              width: 32,
              height: 32,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                transform: "scale(1.1)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
      ) : (
        <IconButton
          onClick={onClose}
          size="medium"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 1,
            color: "text.secondary",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            width: 40,
            height: 40,
            "&:hover": {
              backgroundColor: "rgba(244, 67, 54, 0.1)",
              color: "error.main",
              transform: "scale(1.1)",
              boxShadow: "0 4px 12px rgba(244, 67, 54, 0.2)",
            },
            transition: "all 0.3s ease",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      )}

      <DialogContent
        className={genericStyles.genericModalDialogContent}
        sx={{
          padding:
            title && typeof title === "string" && title.trim() !== ""
              ? "24px"
              : "32px 24px 24px",
          background: "background.paper",
          position: "relative",
        }}
      >
        {children}
      </DialogContent>

      {actions && (
        <div
          className={genericStyles.genericModalActionsContainer}
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.02) 100%)",
            borderTop: "1px solid rgba(0, 0, 0, 0.08)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              "& .MuiButton-root": {
                borderRadius: "25px",
                textTransform: "none",
                fontWeight: 500,
                padding: "12px 32px",
                flex: 1,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                },
                "&.MuiButton-contained": {
                  boxShadow: (theme) =>
                    `0 4px 12px ${theme.palette.primary.main}50`,
                  "&:hover": {
                    boxShadow: (theme) =>
                      `0 6px 20px ${theme.palette.primary.main}66`,
                  },
                },
              },
            }}
          >
            {actions}
          </Box>
        </div>
      )}
    </Dialog>
  );
}
